import { Department, DepartmentsListResponse, User } from '../types';
import apiClient from './apiClient';

export interface CreateDepartmentData {
  name: string;
}

export interface UpdateDepartmentData {
  name?: string;
  is_active?: boolean;
}

export interface DepartmentUsersResponse {
  department: Department;
  users: User[];
  total: number;
}

/**
 * Departments API service
 */
export const departmentsApi = {
  /**
   * Get all departments
   */
  getDepartments: async (): Promise<DepartmentsListResponse> => {
    return apiClient.get<DepartmentsListResponse>('/departments');
  },

  /**
   * Get department by ID
   */
  getDepartment: async (deptId: number): Promise<{ department: Department }> => {
    return apiClient.get<{ department: Department }>(`/departments/${deptId}`);
  },

  /**
   * Get users in a department
   */
  getDepartmentUsers: async (deptId: number, includeInactive?: boolean): Promise<DepartmentUsersResponse> => {
    const params = new URLSearchParams();
    if (includeInactive) params.append('include_inactive', 'true');
    const query = params.toString();
    return apiClient.get<DepartmentUsersResponse>(`/departments/${deptId}/users${query ? `?${query}` : ''}`);
  },

  /**
   * Export users in a department
   */
  exportDepartmentUsers: async (deptId: number, params?: {
    format?: 'csv' | 'xlsx';
    include_inactive?: boolean;
    columns?: string;
  }): Promise<Blob> => {
    const queryParams = new URLSearchParams();
    if (params?.format) queryParams.append('format', params.format);
    if (params?.include_inactive) queryParams.append('include_inactive', 'true');
    if (params?.columns) queryParams.append('columns', params.columns);
    const query = queryParams.toString();
    return apiClient.getBlob(`/departments/${deptId}/users/export${query ? `?${query}` : ''}`);
  },

  /**
   * Create a new department
   */
  createDepartment: async (data: CreateDepartmentData): Promise<{ message: string; department: Department }> => {
    return apiClient.post<{ message: string; department: Department }>('/departments', data);
  },

  /**
   * Update department
   */
  updateDepartment: async (deptId: number, data: UpdateDepartmentData): Promise<{ message: string; department: Department }> => {
    return apiClient.put<{ message: string; department: Department }>(`/departments/${deptId}`, data);
  },

  /**
   * Delete department (soft delete)
   */
  deleteDepartment: async (deptId: number): Promise<{ message: string }> => {
    return apiClient.delete<{ message: string }>(`/departments/${deptId}`);
  },
};

export default departmentsApi;
